import styled, { css } from "styled-components";
import { borderRadius } from "../../../design-system/tokens/borders";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import {
  AddAudienceChip,
  AudienceChip,
  AudienceChipRow,
  ModelDropdownList,
} from "../../organisms/StoryBuilder/StoryBuilder.styles";
import { ChipBox } from "../StoryAudienceSection/StoryAudienceSection.styles";

/**
 * `modal` variant wrappers — Figma 5326:88659 (create-data-story details step).
 *
 * The tabs (`SegmentedControl`), the model dropdown and the multi-select list
 * are shared verbatim with the `builder` variant; only the outer frame and the
 * chip shape differ, so only those are (re)declared here. The label + box frame
 * itself comes from `StoryAudienceSection.styles`, which draws the same
 * 5326:88555 box — importing it keeps the two fields pixel-identical by
 * construction rather than by two copies that agree today.
 *
 * The popover positioning at the bottom of this file is shared by both variants
 * (see `SourceListPopover`), since the values it fixes are not frame-specific.
 */

/** Figma 5326:88547 — tabs and the chip box stack with an 8px gutter. */
export const ModalStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  width: 100%;
  min-width: 0;
`;

/** Figma 5326:88556 — the sources row wraps on a 12px gutter, not the audience
 *  row's 8px, and fills the box so the dashed add-chip wraps with the pills. */
export const SourceChipRow = styled(AudienceChipRow)`
  flex: 1 1 0;
  min-width: 0;
  gap: ${spacing[3]};
`;

/** Figma 5326:88561 — a source reads as a pill: white with a stroke/200
 *  hairline, where an audience is a filled structural/100 tag. */
export const SourcePill = styled(AudienceChip)`
  background: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.full};
`;

/** Figma 5326:88610 — the dashed add-chip follows the pills into radius/full. */
export const AddSourcePill = styled(AddAudienceChip)`
  border-radius: ${borderRadius.full};
`;

/**
 * The `modal` variant's chip box, doubling as the connector popover's anchor.
 *
 * The popover used to hang off the add-chip's own wrapper, which meant its
 * position moved every time a picked chip made the row wrap. Anchoring to the
 * box instead makes the position independent of how many chips are in the row:
 * the box is what stays still.
 */
export const SourcesChipBox = styled(ChipBox)`
  position: relative;
`;

/**
 * How tall the popover may grow before it scrolls itself.
 *
 * `min()` of a fixed height and a viewport fraction: 14rem is ~5 rows, which
 * covers a typical workspace without scrolling, and the `40vh` term keeps the
 * list on screen on a short viewport, where the shell itself is already capped
 * at 90vh.
 */
const POPOVER_MAX_HEIGHT = "min(14rem, 40vh)";

/**
 * Drops the popover below its anchor with its own scrollbar.
 *
 * Downward, and overlaying the footer, is only possible because the two details
 * steps set `bodyOverflowY: "visible"` on the shell. That matters more than it
 * looks: a scroll container clips its descendants no matter what z-index they
 * carry, so while the body was `overflow-y: auto` there was *no* placement that
 * showed a long list in full — upward ran into the body's top edge, downward
 * into its bottom one. Making the body not clip is what fixed that; this rule
 * just picks the conventional direction now that the choice is free.
 *
 * `overflow: hidden auto` — vertical scroll, no horizontal one. A long
 * connector name should ellipsise or wrap inside the row, never widen the list.
 */
const dropBelowWithOwnScroll = css`
  top: calc(100% + ${spacing[1]});
  bottom: auto;
  max-height: ${POPOVER_MAX_HEIGHT};
  overflow: hidden auto;
`;

/**
 * The connectors popover hangs from its anchor's left edge and sizes to its own
 * content, unlike the model dropdown which spans the full field width.
 */
const anchoredLeft = css`
  left: 0;
  right: auto;
  min-width: 13.75rem;
`;

/** The connectors multi-select popover, `modal` variant. */
export const SourceListPopover = styled(ModelDropdownList)`
  ${dropBelowWithOwnScroll}
  ${anchoredLeft}
`;

/**
 * The connectors multi-select popover, `builder` variant.
 *
 * The canvas has no scroll container clipping it and no footer to clear, so
 * this keeps the plain placement it has always had. Declared here only to
 * replace the inline `px` style prop it previously carried.
 */
export const BuilderSourceListPopover = styled(ModelDropdownList)`
  ${anchoredLeft}
`;

/** The data-model dropdown, `modal` variant — same field, same reasoning: it
 *  needs to clear the footer to show its whole list. */
export const ModelListPopover = styled(ModelDropdownList)`
  ${dropBelowWithOwnScroll}
`;

/** Anchor width fix for the add-chip wrapper in the `builder` variant, which
 *  must hug the chip rather than fill the row. Was an inline style prop. */
export const AddSourceAnchor = styled.div`
  position: relative;
  width: auto;
`;
